//
// Copyright (c) 2011-2023 by Curalate, Inc.
//

import Cuid from '@models/Cuid';
import DateString from '@models/DateString';
import { IMlsMediaItem } from '@models/IMlsMediaItem';
import ItemId from '@models/ItemId';
import TikTokUserId from '@models/TikTokUserId';
import TimeZoneId from '@models/TimeZoneId';
import { WeekDay } from '@utils/moment';

export const TIKTOK_API_DATE_FORMAT = 'YYYY MM DD';

export interface IMaterializedTikTokVideoWithMetrics {
  cuid: Cuid;
  mediaItem?: IMlsMediaItem;
  tiktokItem?: ITikTokMedia;
  metrics?: ITikTokMediaMetrics;
}

export interface ITikTokMedia {
  id: ItemId;
  audienceCountries?: ITikTokMediaAudienceCountry[];
  averageTimeWatched?: number;
  caption?: string;
  commentCount?: number;
  coverImageUrl?: string;
  createdTime?: number;
  durationSeconds?: number;
  embedUrl?: string;
  fullVideoWatchedRate?: number;
  impressionSources?: ITikTokMediaImpressionSource[];
  likeCount?: number;
  posterUserId?: string;
  reach?: number;
  shareCount?: number;
  shareUrl?: string;
  totalTimeWatched?: number;
  viewCount?: number;
}

export interface ITikTokMediaAudienceCountry {
  country?: string;
  percentage?: number;
}

export interface ITikTokMediaImpressionSource {
  impressionSource?: string;
  percentage?: number;
}

export interface ITikTokMediaMetrics {
  comments?: number;
  engagementRate?: number;
  likes?: number;
  reach?: number;
  shares?: number;
  views?: number;
}

export interface ITikTokEngagementStats {
  dailyMetrics: ITikTokEngagementDailyStats[];
  summaryMetrics: ITikTokEngagementMetrics;
}

export interface ITikTokEngagementMetrics {
  comments?: number;
  engagementRate?: number;
  likes?: number;
  reach?: number;
  shares?: number;
  views?: number;
}

export enum TikTokMetricSortKey {
  PostedTimestamp = 'PostedTimestamp',
  Views = 'Views',
  Reach = 'Reach',
  EngagementRate = 'EngagementWithReach',
  Likes = 'Likes',
  Comments = 'Comments',
  Shares = 'Shares',
  WatchRate = 'FullVideoWatchedRate',
}

export interface ITikTokAudienceActivity {
  hour: string;
  count: number;
}

export interface ITikTokEngagementDailyStats {
  date: DateString;
  metrics: ITikTokEngagementMetrics;
}

export interface ITikTokAudienceDailyMetrics {
  changeInFollowers?: number;
  followersCount?: number;
  profileViews?: number;
  videoViews?: number;
  likeCount?: number;
  commentCount?: number;
  shareCount?: number;
  audienceActivity: ITikTokAudienceActivity[];
}

export interface ITikTokAudienceSummaryStats {
  changeInFollowers?: number;
  followersCount?: number;
  profileViews?: number;
  videoViews?: number;
  likeCount?: number;
  commentCount?: number;
  shareCount?: number;
  audienceActivity: ITikTokAudienceActivity[];
}

export interface IGetEngagementStatsRequest {
  tiktokUsername: string;
  tiktokDisplayName?: string;
  tiktokUserId: TikTokUserId;
  startTime: DateString;
  endTime: DateString;
}

export interface IGetEngagementStatsResponse {
  data: ITikTokEngagementStats;
}

export interface IGetEngagementVideosRequest {
  tiktokUsername: string;
  tiktokDisplayName?: string;
  tiktokUserId: TikTokUserId;
  startTime?: DateString;
  endTime: DateString;
  limit?: number;
  offset?: number;
  sortKey?: TikTokMetricSortKey;
  isOrderAscending?: boolean;
}

export interface IGetDailyMetricsRequest {
  tiktokUserId: TikTokUserId;
  startTime: Date;
  endTime: Date;
}

export interface IGetDailyMetricsResponse {
  data: IGetDailyMetricsResult;
}

export interface IGetDailyMetricsResult {
  dailyMetrics: Record<string, ITikTokAudienceDailyMetrics>;
  earliestDate?: Date;
}

export interface IGetSummaryMetricsRequest {
  tiktokUserId: TikTokUserId;
  startTime: Date;
  endTime: Date;
}

export interface IGetSummaryMetricsResponse {
  data: ITikTokAudienceDailyMetrics;
}

export interface IGetEngagementVideosResponse {
  data: IGetEngagementVideosResult;
}

export interface IGetEngagementVideosResult {
  videos: IMaterializedTikTokVideoWithMetrics[];
  total: number;
}

export interface IGetAudienceOnlineFollowersHeatmapRequest {
  tiktokUserId: TikTokUserId;
  timeZone: TimeZoneId;
}

export interface IGetAudienceOnlineFollowersHeatmapResponse {
  data: ITikTokOnlineFollowersHeatmap;
}

/**
 * `onlineFollowers` is an array with length 24 for each hour of the day.
 * Each hour array consists of one `DailyFollowerDataForOneHour`, which contains
 * the count of `onlineFollowers` and its `percentage` compared to the rest of the data
 * for 7 days.
 *
 */
export interface ITikTokOnlineFollowersHeatmap {
  onlineFollowers?: TikTokDailyFollowerDataForOneHour[];
}

export interface ITikTokFollowerData {
  followerCount: number;
  percentage: number;
}

export type TikTokDailyFollowerDataForOneHour = Record<
  WeekDay,
  ITikTokFollowerData
>;

export interface IGetAudienceBestTimesToPostRequest {
  tiktokUserId: TikTokUserId;
  timeZone: TimeZoneId;
}

export interface IGetAudienceBestTimesToPostResponse {
  data: ITikTokBestTimesToPost;
}

export interface ITikTokBestTimesToPost {
  bestTimes?: TikTokWeeklyBestTimesToPost;
}

export type TikTokWeeklyBestTimesToPost = Record<
  WeekDay,
  ITikTokBestTimeToPost[]
>;

export interface ITikTokBestTimeToPost {
  followerCount: number;
  hourOfDay: number;
  rank: 1 | 2 | 3;
}
